import React, { useContext, useState } from 'react';
import { useStyletron } from 'baseui';
import { ButtonGroup } from 'baseui/button-group';
import { Heading, HeadingLevel } from 'baseui/heading';
import { Modal, ModalBody, ModalButton, ModalFooter, ModalHeader, ROLE, SIZE } from 'baseui/modal';
import { Select } from 'baseui/select';
import { toaster } from 'baseui/toast';
import { Button, ButtonRow } from '@gtintel/platform-atoms';
import ProjectAccountingModal from '../../components/AccountingModal';
import { AdjustmentModal } from '../../components/Adjustments';
import AttachmentFiles from '../../components/AttachmentFiles';
import { RequestMatchButton } from '../../components/Buttons';
import { ProjectFlagsAccordion } from '../../components/ProjectFlagsAccordion';
import ReportBuilderMagicLink from '../../components/ReportBuilderMagicLink';
import { StatusDisplay } from '../../components/StatusDisplay';
import { Title } from '../../components/Title';
import { Services } from '../Services';
import { ProjectContext } from '../context';
import ActionButtons from './ActionButtons';
import ProjectDetailsCard from './ProjectDetails';
import { Subjects } from './Subjects';
import * as S from './styles';
export const jurisdictionComparator = (a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase());
export function Admin() {
  const [css] = useStyletron();
  const projectCtx = useContext(ProjectContext);
  const [isCreatingSubProject, setIsCreatingSubProject] = useState(false);
  const [hasCreationFailed, setHasCreationFailed] = useState(false);
  const [isClearingTemplate, setIsClearingTemplate] = useState(false);
  const [isAccountingModalVisible, setIsAccountingModalVisible] = useState(false);
  const [isAdjustmentModalVisible, setIsAdjustmentModalVisible] = useState(false);
  const [isTemplateClearingModalVisible, setIsTemplateClearingModalVisible] = useState(false);
  const [isCreateSubprojectModalVisible, setIsCreateSubprojectModalVisible] = useState(false);
  const clickCreateSubProject = async () => {
    setIsCreatingSubProject(prevState => !prevState);
    setHasCreationFailed(false);
    projectCtx.createSubProject().then(() => {
      toaster.positive(<>Successfully created sub-project! Navigating to it...</>);
      setIsTemplateClearingModalVisible(false);
    }).catch(e => {
      console.error(e);
      toaster.negative(<>
            An error occurred when creating the sub-project. Please try again
            and if you have any more problems, raise an incident.
          </>);
      setIsCreatingSubProject(prevState => !prevState);
      setHasCreationFailed(true);
    });
  };
  return <section className={css({
    padding: '26px 44px',
    '@media (max-width: 768px)': {
      padding: '0'
    }
  })}>
      <HeadingLevel>
        <div className={css({
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '28px',
        '@media only screen and (max-width: 768px)': {
          flexDirection: 'column',
          gap: '8px'
        },
        h1: {
          flexGrow: 2,
          marginBottom: 0,
          marginRight: '14px'
        }
      })} data-testid="project-name-label">
          <Heading>
            <Title text={projectCtx.name()} onChange={name => projectCtx.edit({
            name
          })} />
          </Heading>
          {<StatusDisplay status={projectCtx.status()} />}
        </div>

        <ReportBuilderMagicLink projectId={Number(projectCtx.projectId())} />

        {projectCtx.isEmpty() && <div className={css({
        marginBottom: '1em'
      })} data-testid="import-template-select">
            <Select placeholder="Import Template..." options={projectCtx.templateOptions()} onChange={async ({
          value: [{
            id
          }]
        }) => {
          projectCtx.importTemplate(id.toString());
        }} />
          </div>}
        <ProjectDetailsCard />

        <S.ButtonRow>
          <ButtonGroup>
            <Button data-testid="accounting-button" disabled={parseInt(projectCtx.status()) < 4} onClick={() => setIsAccountingModalVisible(!isAccountingModalVisible)}>
              Accounting
            </Button>

            {parseInt(projectCtx.status()) > 3 && <Button onClick={() => setIsAdjustmentModalVisible(true)}>
                Adjustments
              </Button>}
          </ButtonGroup>

          <RequestMatchButton />
          <Button disabled={isCreatingSubProject || hasCreationFailed} loading={isCreatingSubProject} onClick={() => setIsCreateSubprojectModalVisible(true)}>
            {hasCreationFailed ? 'Failed previously. Refresh to try again' : 'Copy to Sub-Project'}
          </Button>
        </S.ButtonRow>

        <ActionButtons />

        {projectCtx.flags() ? <ProjectFlagsAccordion /> : <div>
            <HeadingLevel>
              <Heading styleLevel={4}>Flags</Heading>
            </HeadingLevel>
            <p>
              There are no flags for this project. Would you like to create
              them?
            </p>
            <Button onClick={() => projectCtx.createFlags()}>Create</Button>
          </div>}

        <Heading>Subjects</Heading>
        <Subjects />

        <Heading>Scope</Heading>

        <ButtonRow flexPosition="flex-end">
          <Button kind="error" loading={isClearingTemplate} onClick={() => setIsTemplateClearingModalVisible(true)}>
            Clear
          </Button>
        </ButtonRow>

        <AttachmentFiles isNP={false} />

        <Services />

        <Modal onClose={() => setIsTemplateClearingModalVisible(true)} isOpen={isTemplateClearingModalVisible} closeable animate size={SIZE.auto} role={ROLE.dialog}>
          <ModalHeader>
            Do you want to clear all scope related data?
          </ModalHeader>

          <ModalBody>
            <p>Clearing will remove:</p>
            <ul>
              <li>
                <b>{projectCtx.scopesLength()}</b> scope items
              </li>
              {projectCtx.assignmentsCount() > 0 && <li>
                  <b>{projectCtx.assignmentsCount()}</b> assignments scopes
                </li>}
              {projectCtx.sampleReport() && <li>A sample report</li>}
              {projectCtx.reportTemplate() && <li>A report template</li>}
              <li>and any instructions</li>
            </ul>
            <p>However, you will be able to re-import from any template</p>
          </ModalBody>

          <ModalFooter>
            <ModalButton data-testid="accept-template-clear-button" onClick={() => {
            setIsClearingTemplate(true);
            setIsTemplateClearingModalVisible(false);
            projectCtx.clearScopes().catch(e => {
              console.error(e.response.data.message);
              console.error(e.message);
              toaster.negative(e.response.data.message);
            });
            setIsClearingTemplate(false);
          }}>
              Clear
            </ModalButton>
            <ModalButton data-testid="cancel-template-clear-button" onClick={() => setIsTemplateClearingModalVisible(false)} kind="secondary">
              Cancel
            </ModalButton>
          </ModalFooter>
        </Modal>

        <Modal onClose={() => setIsCreateSubprojectModalVisible(true)} isOpen={isCreateSubprojectModalVisible} closeable animate size={SIZE.auto} role={ROLE.dialog}>
          <ModalHeader>Sub-Project Creation</ModalHeader>

          <ModalBody>
            <p>Are you sure you want to create a sub-project?</p>
            <p>
              This will copy over all related documents and subjects but will
              remove all scope information
            </p>
          </ModalBody>

          <ModalFooter>
            <ModalButton data-testid="accept-create-subproject-button" disabled={isCreatingSubProject || hasCreationFailed} loading={isCreatingSubProject} onClick={() => {
            clickCreateSubProject();
          }}>
              Create
            </ModalButton>
            <ModalButton data-testid="cancel-create-subproject-button" onClick={() => setIsCreateSubprojectModalVisible(false)} kind="secondary">
              Cancel
            </ModalButton>
          </ModalFooter>
        </Modal>

        <ProjectAccountingModal projectId={projectCtx.parent() ? +projectCtx.parent().id : +projectCtx.projectId()} hideModal={() => setIsAccountingModalVisible(false)} isVisible={isAccountingModalVisible} />

        <AdjustmentModal hideModal={() => setIsAdjustmentModalVisible(false)} isVisible={isAdjustmentModalVisible} />
      </HeadingLevel>
    </section>;
}